'use client';

/**
 * ObeyaProjectsListSection — RAG-filtered projects list with tender
 * groups + MCP children.
 *
 * Schema-driven extraction of the legacy `<ProjectsList />` /
 * `<ObeyaProjectItem />` / `<RfqTenderList />` / `<TenderGroup />` /
 * `<ChildSection />` / `<ChildRow />` / `<McpRow />` cluster from the
 * old monolithic `obeya-explorer.tsx`. Same data, same logic, same UI
 * — just declared by `EXPLORER_SCHEMA` rather than inlined.
 *
 * Gated on `useObeyaExplorer().subMode === 'projects'`. Renders
 * nothing when the user toggles to Simulations (the narrative-search
 * sections take over).
 *
 * @see klyde_edge/ui_config/entity_schema.py — ExplorerSectionType.OBEYA_PROJECTS_LIST
 *
 * @component_role: Section
 * @component_type: Section Renderer
 * @domain: Frontend / Explorer / Obeya
 * @layer: presentation
 */

import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  DollarSign,
  Download,
  FileText,
  LayoutDashboard,
  Loader2,
  MoreVertical,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useObeyaProjects, useProjectSubfolders } from '@/lib/api/hooks';
import { useTabStore } from '@/lib/stores/tab-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useMcpActions } from '@/lib/hooks/use-mcp-actions';
import { cn } from '@/lib/utils/cn';
import { useObeyaExplorer } from '../providers/obeya-explorer-provider';
import type {
  ExplorerSectionConfig,
  LabelsDict,
  RAGStatus,
  ObeyaListItem,
  ProjectExploreData,
  McpSummary,
} from '@/lib/api/types';

const RAGIcon = {
  red: AlertCircle,
  amber: AlertTriangle,
  green: CheckCircle,
  unknown: LayoutDashboard,
};

const RAGColor = {
  red: 'text-red-500',
  amber: 'text-amber-500',
  green: 'text-green-500',
  unknown: 'text-muted-foreground',
};

const RAGPulse = {
  red: 'animate-pulse',
  amber: '',
  green: '',
  unknown: '',
};

interface ObeyaProjectsListSectionProps {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}

export function ObeyaProjectsListSection(_props: ObeyaProjectsListSectionProps) {
  const { subMode } = useObeyaExplorer();
  if (subMode !== 'projects') return null;
  return <ProjectsList />;
}

function ProjectsList() {
  const [filter] = useState<RAGStatus | 'all'>('all');
  const listStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const lang = useLanguageStore((s) => s.lang);
  const { data, isLoading, error } = useObeyaProjects(listStrategy!, {
    limit: 50,
    rag_filter: filter === 'all' ? undefined : filter,
    ...(lang ? { lang } : {}),
  });
  const L = data?.labels ?? {};

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-sm text-destructive">{L.load_failed}</div>;
  }

  if (!data?.projects.length) {
    return <div className="p-4 text-sm text-muted-foreground">{L.no_projects}</div>;
  }

  const counts = {
    red: data.summary.red,
    amber: data.summary.amber,
    green: data.summary.green,
  };

  return (
    <div className="flex flex-col">
      {counts.red > 0 && (
        <div className="px-3 py-2 bg-red-500/10 border-b border-red-500/20 text-xs text-red-400">
          <AlertCircle className="w-3 h-3 inline mr-1" />
          {counts.red} {L.require_attention}
        </div>
      )}

      <div className="py-1">
        {data.projects.map((project) => (
          <ObeyaProjectItem key={project.semantic_id} project={project} />
        ))}
      </div>

      <div className="px-2 py-1 border-t border-border/50 text-xs text-muted-foreground">
        {data.metadata.rules_applied} {L.rules} • {data.metadata.elapsed_ms}{L.ms}
      </div>
    </div>
  );
}

type ChildItem = ProjectExploreData['rfqs'][number] | ProjectExploreData['pos'][number];

function ObeyaProjectItem({ project }: { project: ObeyaListItem }) {
  const listStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const lang = useLanguageStore((s) => s.lang);
  const { data: labelsData } = useObeyaProjects(listStrategy!, {
    limit: 50,
    ...(lang ? { lang } : {}),
  });
  const L = labelsData?.labels ?? {};

  const [expanded, setExpanded] = useState(false);
  const hasChildren =
    project.rfq_count > 0 || project.po_count > 0 || project.mcp_count > 0;
  const openTab = useTabStore((state) => state.openTab);
  const activeMode = useLayoutStore((s) => s.activeExplorerView);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setProjectContext = useGraphSelectionStore((s) => s.setProjectContext);
  const selectedProjectId = useGraphSelectionStore((s) => s.projectId);

  // Auto-expand when this project becomes the active selection from
  // anywhere — explorer click, canvas click, or graph node click all
  // route through graph-selection-store.projectId. Expand-on-match,
  // never collapse-on-mismatch (preserves the user's explore state
  // for projects they've expanded manually).
  useEffect(() => {
    if (selectedProjectId === project.semantic_id && hasChildren) {
      setExpanded(true);
    }
  }, [selectedProjectId, project.semantic_id, hasChildren]);

  const status = project.rag_status;
  const StatusIcon = RAGIcon[status];

  const exploreStrategy = useCapabilityStore((s) =>
    s.strategyName('project_intelligence', 'explore'),
  );
  const { data: response, isLoading: childrenLoading } = useProjectSubfolders(
    exploreStrategy!,
    expanded ? project.semantic_id : null,
  );
  const exploreData = response?.data as ProjectExploreData | undefined;
  const rfqs = exploreData?.rfqs ?? [];
  const mcps = exploreData?.mcps ?? [];

  const [pendingAutoSelect, setPendingAutoSelect] = useState(false);

  useEffect(() => {
    if (pendingAutoSelect && rfqs.length > 0) {
      const firstRfq = rfqs[0];
      selectNode(
        {
          id: firstRfq.semantic_id || '',
          label: firstRfq.name || 'RFQ',
          type: 'rfq',
          status: firstRfq.state || undefined,
          properties: firstRfq as unknown as Record<string, unknown>,
        },
        project.semantic_id,
      );
      setPendingAutoSelect(false);
    }
  }, [pendingAutoSelect, rfqs, selectNode, project.semantic_id]);

  const handleOpenProject = (e: React.MouseEvent) => {
    e.stopPropagation();
    setProjectContext(project.semantic_id, {
      name: project.name,
      rag_status: project.rag_status,
      rag_label: project.rag_label,
      rfq_count: project.rfq_count,
      po_count: project.po_count,
    });
    openTab({
      id: `${activeMode}-${project.semantic_id}`,
      type: 'project',
      title: project.name,
      entityId: project.semantic_id,
    });
    if (hasChildren) {
      setExpanded(true);
      setPendingAutoSelect(true);
    }
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) setExpanded(!expanded);
  };

  return (
    <div>
      <div
        className={cn(
          'w-full flex items-center gap-1 px-2 py-1.5 text-sm',
          'hover:bg-accent/50 transition-colors cursor-pointer',
          status === 'red' && 'bg-red-500/5',
          !hasChildren && 'pl-6',
        )}
      >
        {hasChildren && (
          <button onClick={handleToggleExpand} className="shrink-0">
            <ChevronRight
              className={cn(
                'w-4 h-4 transition-transform text-muted-foreground',
                expanded && 'rotate-90',
              )}
            />
          </button>
        )}
        <button
          onClick={handleOpenProject}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          <StatusIcon
            className={cn('w-4 h-4 shrink-0', RAGColor[status], RAGPulse[status])}
          />
          <div className="flex-1 min-w-0">
            <span className="truncate block">{project.name}</span>
            <span className="text-xs text-muted-foreground truncate block">
              {project.rag_label}
            </span>
          </div>
        </button>
        <span className="text-xs text-muted-foreground shrink-0">
          {project.rfq_count > 0 && `${project.rfq_count} RFQ`}
        </span>
      </div>

      {expanded && (
        <div className="ml-4 border-l border-border/50">
          {childrenLoading ? (
            <div className="flex items-center gap-2 px-4 py-1 text-sm text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              {L.loading}
            </div>
          ) : (
            <>
              {rfqs.length > 0 && (
                <ChildSection
                  label={L.rfqs}
                  icon={FileText}
                  iconColor="text-yellow-400"
                  count={rfqs.length}
                >
                  <RfqTenderList
                    rfqs={rfqs}
                    projectSid={project.semantic_id}
                    selectNode={selectNode}
                  />
                </ChildSection>
              )}
              {mcps.length > 0 && (
                <ChildSection
                  label={L.cost_plans}
                  icon={DollarSign}
                  iconColor="text-amber-400"
                  count={mcps.length}
                >
                  {mcps.map((mcp) => (
                    <McpRow
                      key={mcp.semantic_id}
                      mcp={mcp}
                      projectSid={project.semantic_id}
                      selectNode={selectNode}
                    />
                  ))}
                </ChildSection>
              )}
              {rfqs.length === 0 && mcps.length === 0 && (
                <div className="px-4 py-1 text-xs text-muted-foreground">
                  {L.no_items}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Group RFQs by x_rfq_group_id; render single-vendor tenders flat,
 *  multi-vendor tenders as expandable group headers. */
function RfqTenderList({
  rfqs,
  projectSid,
  selectNode,
}: {
  rfqs: ProjectExploreData['rfqs'];
  projectSid: string;
  selectNode: (
    node: {
      id: string;
      label: string;
      type: string;
      status?: string;
      properties: Record<string, unknown>;
    },
    projectId: string,
  ) => void;
}) {
  const groups = useMemo(() => {
    const out: Array<{
      key: string;
      x_rfq_group_id: string | null;
      x_rfq_group_name: string | null;
      rfqs: ProjectExploreData['rfqs'];
    }> = [];
    const idx: Record<string, number> = {};
    for (const rfq of rfqs) {
      const key = rfq.x_rfq_group_id || rfq.semantic_id;
      if (idx[key] === undefined) {
        idx[key] = out.length;
        out.push({
          key,
          x_rfq_group_id: rfq.x_rfq_group_id,
          x_rfq_group_name: rfq.x_rfq_group_name,
          rfqs: [],
        });
      }
      out[idx[key]].rfqs.push(rfq);
    }
    return out;
  }, [rfqs]);

  return (
    <>
      {groups.map((g) =>
        g.rfqs.length === 1 || !g.x_rfq_group_id ? (
          <ChildRow
            key={g.key}
            item={g.rfqs[0]}
            type="rfq"
            icon={FileText}
            iconColor="text-yellow-400"
            projectSid={projectSid}
            selectNode={selectNode}
          />
        ) : (
          <TenderGroup
            key={g.key}
            rfqGroup={g.x_rfq_group_id as string}
            rfqGroupName={g.x_rfq_group_name}
            rfqs={g.rfqs}
            projectSid={projectSid}
            selectNode={selectNode}
          />
        ),
      )}
    </>
  );
}

function TenderGroup({
  rfqGroup,
  rfqGroupName,
  rfqs,
  projectSid,
  selectNode,
}: {
  rfqGroup: string;
  rfqGroupName: string | null;
  rfqs: ProjectExploreData['rfqs'];
  projectSid: string;
  selectNode: (
    node: {
      id: string;
      label: string;
      type: string;
      status?: string;
      properties: Record<string, unknown>;
    },
    projectId: string,
  ) => void;
}) {
  const [open, setOpen] = useState(false);
  const tenderLabel =
    rfqGroupName ?? `Tender ${rfqGroup.split('/').slice(-1)[0] ?? rfqGroup}`;

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1 px-2 py-1 text-sm text-left hover:bg-accent/50 pl-6"
      >
        <ChevronRight
          className={cn(
            'w-3 h-3 shrink-0 transition-transform text-muted-foreground',
            open && 'rotate-90',
          )}
        />
        <FileText className="w-4 h-4 shrink-0 text-yellow-400" />
        <span className="truncate flex-1 font-medium">{tenderLabel}</span>
        <span className="text-xs text-muted-foreground">{rfqs.length} vendors</span>
      </button>
      {open && (
        <div className="ml-3 border-l border-border/30">
          {rfqs.map((rfq) => (
            <ChildRow
              key={rfq.semantic_id}
              item={rfq}
              type="rfq"
              icon={FileText}
              iconColor="text-yellow-400"
              projectSid={projectSid}
              selectNode={selectNode}
              labelOverride={rfq.vendor_name || rfq.name}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChildSection({
  label,
  icon: Icon,
  iconColor,
  count,
  children,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  count: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronRight
          className={cn(
            'w-3 h-3 shrink-0 transition-transform',
            open && 'rotate-90',
          )}
        />
        <Icon className={cn('w-3 h-3 shrink-0', iconColor)} />
        <span className="font-medium">{label}</span>
        <span className="ml-auto text-[10px] bg-muted rounded-full px-1.5">
          {count}
        </span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

function ChildRow({
  item,
  type,
  icon: Icon,
  iconColor,
  projectSid,
  selectNode,
  labelOverride,
}: {
  item: ChildItem;
  type: 'rfq' | 'po';
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  projectSid: string;
  selectNode: (
    node: {
      id: string;
      label: string;
      type: string;
      status?: string;
      properties: Record<string, unknown>;
    },
    projectId: string,
  ) => void;
  labelOverride?: string;
}) {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);
  const label = labelOverride || item.name;

  return (
    <button
      onClick={() =>
        selectNode(
          {
            id: item.semantic_id || '',
            label: item.name || type.toUpperCase(),
            type,
            status: item.state || undefined,
            properties: item as unknown as Record<string, unknown>,
          },
          projectSid,
        )
      }
      className={cn(
        'w-full flex items-center gap-1 px-2 py-1 text-sm text-left hover:bg-accent/50 pl-6',
        selectedNode?.id === item.semantic_id && 'bg-accent',
      )}
    >
      <Icon className={cn('w-4 h-4 shrink-0', iconColor)} />
      <span className="truncate flex-1">{label}</span>
      <span className="text-xs text-muted-foreground">{item.state}</span>
    </button>
  );
}

function McpRow({
  mcp,
  projectSid,
  selectNode,
}: {
  mcp: McpSummary;
  projectSid: string;
  selectNode: (
    node: {
      id: string;
      label: string;
      type: string;
      properties: Record<string, unknown>;
    },
    projectId: string,
  ) => void;
}) {
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);
  const { editMcp, deleteMcp, downloadMcp, downloadMcpSchedule, downloadMcpScheduleTypst } = useMcpActions();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      className={cn(
        'group relative w-full flex items-center gap-1 px-2 py-1 text-sm hover:bg-accent/50 pl-6',
        selectedNode?.id === mcp.semantic_id && 'bg-accent',
      )}
    >
      <button
        type="button"
        onClick={() =>
          selectNode(
            {
              id: mcp.semantic_id,
              label: mcp.name || 'Cost Plan',
              type: 'mcp',
              properties: mcp as unknown as Record<string, unknown>,
            },
            projectSid,
          )
        }
        className="flex items-center gap-1 flex-1 min-w-0 text-left"
      >
        <DollarSign className="w-4 h-4 shrink-0 text-amber-400" />
        <span className="truncate flex-1">{mcp.name}</span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        aria-label="Cost plan actions"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute right-1 top-7 z-50 w-36 rounded-md border border-border bg-popover shadow-md py-1 text-sm"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void editMcp(mcp.semantic_id);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadMcp(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <DollarSign className="w-3.5 h-3.5" /> Cost Plan
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadMcpSchedule(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <FileText className="w-3.5 h-3.5" /> Project Schedule
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void downloadMcpScheduleTypst(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left"
            >
              <FileText className="w-3.5 h-3.5" /> Schedule (Typst)
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void deleteMcp(mcp.semantic_id, mcp.name);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-destructive hover:bg-destructive/10 text-left"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}
